import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { TokenStatus } from 'gql/index';
import { TokenPurposeContextGraphQl } from 'gql/index';
import { AccessLinkExpiredAlert } from './components/AccessLinkExpiredAlert';

interface WorkflowSubmitContentProps {
  context: TokenPurposeContextGraphQl;
  onAction: () => void;
  onRenew: () => void;
  isRenewing: boolean;
}

export const WorkflowSubmitContent: React.FC<WorkflowSubmitContentProps> = ({
  context,
  onAction,
  onRenew,
  isRenewing,
}) => {
  const { formatMessage } = useIntl();

  if (context.status === TokenStatus.Completed) {
    return (
      <Stack spacing={3} p={3}>
        <Alert severity="success">
          {formatMessage({ id: 'This workflow step has already been completed.' })}
        </Alert>
        {context.initiatorName && (
          <Typography variant="body1">
            {formatMessage({ id: 'Requested by {name}' }, { name: context.initiatorName })}
          </Typography>
        )}
        {context.resourceName && (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'Workflow: {name}' }, { name: context.resourceName })}
          </Typography>
        )}
      </Stack>
    );
  }

  if (context.status === TokenStatus.Expired) {
    return (
      <Stack spacing={3} p={3}>
        <AccessLinkExpiredAlert message={context.errorMessage} />
        {context.initiatorName && (
          <Typography variant="body1">
            {formatMessage(
              { id: '{name} needs your action on a workflow' },
              { name: context.initiatorName }
            )}
          </Typography>
        )}
        {context.resourceName && (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'Workflow: {name}' }, { name: context.resourceName })}
          </Typography>
        )}
        {context.customMessage && (
          <Typography variant="body2">
            {context.customMessage}
          </Typography>
        )}
        {context.isRenewable && (
          <Button
            variant="contained"
            onClick={onRenew}
            disabled={isRenewing}
            fullWidth
          >
            {isRenewing ? (
              <CircularProgress size={24} />
            ) : (
              formatMessage({ id: 'Request New Access Link' })
            )}
          </Button>
        )}
      </Stack>
    );
  }

  if (context.status === TokenStatus.Invalid) {
    return (
      <Stack spacing={3} p={3}>
        <Alert severity="error">
          {context.errorMessage || formatMessage({ id: 'This access link is no longer valid.' })}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} p={3}>
      {context.initiatorName && (
        <Typography variant="body1" color="text.primary">
          {formatMessage(
            { id: '{name} needs your action on a workflow' },
            { name: context.initiatorName }
          )}
        </Typography>
      )}
      {context.resourceName && (
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {context.resourceName}
        </Typography>
      )}
      {context.actionDescription && (
        <Typography variant="body2" color="text.secondary">
          {context.actionDescription}
        </Typography>
      )}
      {context.customMessage && (
        <Typography variant="body2" color="text.secondary">
          {context.customMessage}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={onAction}
        fullWidth
        size="large"
      >
        {formatMessage({ id: 'Continue Workflow' })}
      </Button>
    </Stack>
  );
};
